import React, { useEffect, useState, useRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Box } from 'reflexbox/styled-components'
import * as ReactIs from 'react-is'
import assign from 'lodash/assign'

import { H1 } from 'lib/ui/primitives'

import theme from 'theme'
import media, { pixels } from 'media'

import Section from 'components/page/Section'
import Button, { CLASS_NAME as BUTTON_CLASS_NAME } from 'components/Button'
import AICallout from 'components/AICallout'

import { isBrowser, isHTMLDocument } from 'helpers'

export const ID_HERO = 'anvil-hero'

// TODO: style h1, h2, and most things via primitives + proper styled components theming

const getTitleMaxWidth = (verticalBreakpoint) => [
  pixels[verticalBreakpoint] > pixels.tablet || verticalBreakpoint === 'mobile'
    ? '100%'
    : '70%',
  pixels[verticalBreakpoint] >= pixels.tablet ? '100%' : '60%',
  pixels[verticalBreakpoint] >= pixels.desktop ? '100%' : '60%',
  '45%',
  '40%',
]
const getSubtitleMaxWidth = (verticalBreakpoint) => [
  pixels[verticalBreakpoint] > pixels.tablet || verticalBreakpoint === 'mobile'
    ? '100%'
    : '90%',
  pixels[verticalBreakpoint] >= pixels.tablet ? '100%' : '50%',
  pixels[verticalBreakpoint] >= pixels.desktop ? '100%' : '50%',
  '40%',
  '40%',
]

export const AssetContainer = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;

  * {
    z-index: 10;
  }

  ${media.desktop`
    position: static;
  `}
`

const Background = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    [
      'height',
      'color',
      'displayBreakpoint',
      'size',
      'verticalBreakpoint',
    ].indexOf(prop) === -1,
})`
  ${({ displayBreakpoint }) =>
    (displayBreakpoint === 'tablet' || displayBreakpoint === 'desktop') &&
    `display: none;`}
  position: absolute;
  background-color: ${({ color }) => color};
  width: 100vw;
  height: ${({ size }) => (size === 'large' ? '575px' : '370px')};
  bottom: 0;
  left: 0;
  z-index: 5;

  ${({ displayBreakpoint }) =>
    displayBreakpoint &&
    media[displayBreakpoint]`
    display: block;
  `}

  ${media.tablet`
  height: ${({ size }) => (size === 'large' ? '620px' : '300px')};
  `}

  ${({ verticalBreakpoint }) =>
    (!verticalBreakpoint || verticalBreakpoint === 'tablet') &&
    media.desktop`
    bottom: unset;
    left: unset;
    right: 0;
    top: 0;
    width: 250px;
    height: ${({ height }) => height}px;
  `}

  ${({ verticalBreakpoint }) =>
    verticalBreakpoint === 'desktop' &&
    media.desktop`
      height: ${({ size }) => (size === 'large' ? '630px' : '474px')};
    `}

  ${({ verticalBreakpoint }) =>
    (!verticalBreakpoint || verticalBreakpoint === 'tablet') &&
    `
      @media (min-width: 900px) {
        width: 320px;
      }
    `}

  ${media.giant`
    width: 530px;
    bottom: unset;
    left: unset;
    right: 0;
    top: 0;
    height: ${({ height }) => height}px;
  `}
`

const StyledSection = styled(Section)`
  svg {
    // turning off pointer events for the SVG assets so that the button
    // can be clicked. SVG assets are usually 100% width
    pointer-events: none;
  }

  .${BUTTON_CLASS_NAME} {
    z-index: 10;
    position: relative;
  }

  & > .hero-text {
    z-index: 11;
    position: relative;
  }
`

// NOTE: it'd be nice to pass defaultFontSizes here, but it wasnt working.
const getTitleAttrs = ({ verticalBreakpoint, theme }) => ({
  maxWidth: getTitleMaxWidth(verticalBreakpoint),
})

export const Title = styled(H1).attrs(getTitleAttrs)``

export const Eyebrow = styled.span`
  font-weight: ${theme.fontWeights.lightest};
  color: ${theme.colors.black};
  line-height: 27px;
  margin-bottom: 30px;
  display: inline-block;
`

// should NOT be a heading, as it's not the heading of a section.
// this should be big(ish) text that is part of the hero
const getSubtitleAttrs = ({ theme, verticalBreakpoint }) => ({
  fontSize: [
    theme?.fontSizes?.mobile?.body || 24,
    theme?.fontSizes?.mobile?.body || 24,
    theme?.fontSizes?.body || 28,
  ], // TODO: remove hardcoding these fontSizes...
  mt: [40],
  maxWidth: getSubtitleMaxWidth(verticalBreakpoint),
})

export const Subtitle = styled(Box).attrs(getSubtitleAttrs)`
  line-height: 1.4;
`

const Spacer = styled.div`
  margin-top: 50px;
  margin-bottom: 50px;
  border-bottom: 2px solid black;
  width: 63px;

  ${media.desktop`
    margin-top: 64px;
  `}
`

// TODO: write a helper function to convert
// styled-components css to style objects
const descriptionStyle = {
  marginBottom: '50px',
}
const descriptionCss = css`
  margin-bottom: 50px;
`
const getDescriptionAttrs = ({ verticalBreakpoint }) => ({
  maxWidth: getSubtitleMaxWidth(verticalBreakpoint),
})

const Description = styled(Box).attrs(getDescriptionAttrs)`
  ${descriptionCss}
`

// TODO: this only supports 2 items. Probably better if we support any number of buttons, but are we really gonna have more than 2?
// not used here, but used to make it easier on consumers of this component
// to render multiple buttons. up to them though!
export const ButtonContainer = styled.div.attrs({})`
  display: inline-grid;
  margin: 20px 0;
  gap: 20px;
  position: relative;
  z-index: 10;

  ${media.desktop`
    max-width: 400px;
    display: grid;
    grid-template-columns: 1fr 1fr;
  `}
`

const getCTAContainerAttrs = ({ verticalBreakpoint }) => ({
  maxWidth: getSubtitleMaxWidth(verticalBreakpoint),
})
export const CTAContainer = styled(Box).attrs(getCTAContainerAttrs)``

export const PostCTA = styled.div`
  font-size: 15px;
  font-weight: ${theme.fontWeights.light};
  position: relative;
  z-index: 10;
`

function Hero (
  {
    eyebrow,
    title,
    subtitle,
    description, // either a string that will be a p tag, or the react element(s) to render themselves
    renderCTA,
    renderAssets,
    background,
    hideSpacer,
    postCTACallout,
    showAICallout,
    aiCalloutShortened,
    verticalBreakpoint,
    children,
    ...others
  },
  ref
) {
  const heroRef = useRef(null)
  const [heroHeight, setHeroHeight] = useState(null)

  // TODO: consolidate this logic, the imperative handle logic with useContainerHeight hook
  // the imperative handle was for the solutions template  but it's not used anymore.
  // but it's still a good pattern to expore methods on a ref, so we should keep it.
  const handleResize = (e) => {
    if (isHTMLDocument()) {
      const currentHeight = document.getElementById(ID_HERO).offsetHeight
      if (heroHeight !== currentHeight) {
        setHeroHeight(currentHeight)
      }
    }
  }

  useEffect(() => {
    handleResize()
    if (isBrowser()) window.addEventListener('resize', handleResize)
    return () => {
      if (isBrowser()) window.removeEventListener('resize', handleResize)
    }
  }, [])

  // NOTE: this will only expose the handleResize function as 'handleBackgroundRepaint'
  // to the parent component. It will not expose the heroRef. This is intentional,
  // as it helps prevent consumers of this component from doing something they
  // probably don't want to be doing (messing with the DOM in a bad way).
  useImperativeHandle(ref, () => ({
    handleBackgroundRepaint: handleResize,
  }))

  // all this logic is to enable correct rendering of the description
  // in multiple cases: string, react element, and react fragment.
  // we want to keep our dom structure minimal!
  const descriptionAsParagraph = typeof description === 'string'
  const descriptionIsReactElement = ReactIs.isElement(description)
  const descriptionProps = descriptionAsParagraph ? { as: 'p' } : {}

  assign(descriptionStyle, description?.props?.style || {})
  const clonedDescription =
    descriptionIsReactElement &&
    React.cloneElement(description, {
      style: descriptionStyle,
    })

  return (
    <StyledSection {...others} ref={heroRef} id={ID_HERO} name={ID_HERO}>
      {eyebrow && <Eyebrow className="hero-text">{eyebrow}</Eyebrow>}
      <Title
        className="hero-text"
        verticalBreakpoint={verticalBreakpoint}
        defaultFontSizes={{
          mobile: '36px',
          tablet: '36px',
          desktop: '48px',
        }}
      >
        {title}
      </Title>
      {subtitle && (
        <Subtitle className="hero-text" verticalBreakpoint={verticalBreakpoint}>
          {subtitle}
        </Subtitle>
      )}
      {!hideSpacer && <Spacer />}
      {clonedDescription || (
        <Description
          className="hero-text"
          {...descriptionProps}
          verticalBreakpoint={verticalBreakpoint}
        >
          {description}
        </Description>
      )}
      {renderAssets && <AssetContainer>{renderAssets()}</AssetContainer>}
      <CTAContainer
        className="hero-text"
        verticalBreakpoint={verticalBreakpoint}
      >
        {renderCTA && renderCTA()}
        {postCTACallout && <PostCTA>{postCTACallout}</PostCTA>}
        {showAICallout && (
          <AICallout
            style={{ marginTop: '50px' }}
            className="hero-ai-callout"
            shortened={aiCalloutShortened}
          />
        )}
      </CTAContainer>
      {background && (
        <Background
          color={background?.color}
          height={heroHeight}
          displayBreakpoint={background.displayBreakpoint}
          verticalBreakpoint={verticalBreakpoint}
          size={background.size}
        />
      )}
      {children}
    </StyledSection>
  )
}

Hero.defaultProps = {
  renderCTA: () => (
    <Button type="cta" size="huge" withShadow style={{ marginBottom: '20px' }}>
      Get a demo
    </Button>
  ),
  mobileStyleId: 'heroTopRight',
}

Hero.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired, // only strings and fragments!
  subtitle: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
    .isRequired,
  renderCTA: PropTypes.func,
  renderAssets: PropTypes.func.isRequired,
  background: PropTypes.shape({
    color: PropTypes.string.isRequired,
    size: PropTypes.oneOf(['default', 'large']),
    displayBreakpoint: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),
  }),
  // controls when the hero will shift from a horizontal layout to a vertical layout
  verticalBreakpoint: PropTypes.oneOf(['tablet', 'desktop']),
}

export default React.forwardRef(Hero)
